import { useState, useEffect, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Eye, EyeOff, Lock } from 'lucide-react'
import { icons } from 'lucide-react'
import { IconPickerPopover } from './IconPickerPopover'
import { ROUTE_OPTIONS, LOCKED_ROUTE, type BottomTabItem } from './tab-defaults'

interface TabRowProps {
  item: BottomTabItem
  isExpanded: boolean
  onToggleExpand: () => void
  onUpdate: (updates: Partial<BottomTabItem>) => void
  onToggleVisibility: () => void
  canHide: boolean
}

export function TabRow({
  item,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onToggleVisibility,
  canHide,
}: TabRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  }

  // Local label for debounced updates
  const [localLabel, setLocalLabel] = useState(item.label)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Sync localLabel when parent resets (e.g. cancel)
  useEffect(() => {
    setLocalLabel(item.label)
  }, [item.label])

  // Cleanup debounce timer
  useEffect(() => () => clearTimeout(timerRef.current), [])

  function handleLabelChange(value: string) {
    setLocalLabel(value)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => onUpdate({ label: value }), 150)
  }

  const TabIcon = icons[item.icon as keyof typeof icons] ?? icons.CircleAlert
  const isCartRoute = item.route === LOCKED_ROUTE

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-sm transition-colors ${isDragging ? 'shadow-md scale-[1.02]' : ''} ${
        !item.visible ? 'opacity-50' : ''
      }`}
    >
      {/* Collapsed row header */}
      <div
        className={`flex h-12 items-center gap-2 px-2 rounded-sm cursor-pointer transition-colors ${
          isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
        }`}
        role="button"
        tabIndex={0}
        aria-label={`${item.label} tab, ${item.visible ? 'visible' : 'hidden'}`}
        onClick={(e) => {
          // Don't expand if clicking drag handle or eye toggle
          const target = e.target as HTMLElement
          if (target.closest('[data-drag-handle]') || target.closest('[data-visibility-toggle]')) return
          onToggleExpand()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleExpand()
          }
        }}
      >
        {/* Drag handle */}
        <button
          type="button"
          data-drag-handle
          className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none text-gray-400"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          aria-roledescription="sortable"
        >
          <GripVertical className="size-4" />
        </button>

        {/* Icon */}
        <TabIcon className="size-5 flex-shrink-0 text-gray-600" strokeWidth={2} />

        {/* Label */}
        <span className={`flex-1 text-[13px] font-medium ${item.visible ? 'text-gray-900' : 'text-gray-400'}`}>
          {item.label}
        </span>

        {/* Visibility toggle */}
        <button
          type="button"
          data-visibility-toggle
          className={`flex-shrink-0 p-1.5 rounded-md transition-colors ${
            canHide || item.visible ? 'hover:bg-gray-200' : 'cursor-not-allowed'
          }`}
          aria-label={item.visible ? 'ซ่อนแท็บ' : 'แสดงแท็บ'}
          onClick={(e) => {
            e.stopPropagation()
            onToggleVisibility()
          }}
        >
          {item.visible ? (
            <Eye className="size-5 text-gray-400" />
          ) : (
            <EyeOff className="size-5 text-gray-300" />
          )}
        </button>
      </div>

      {/* Expanded edit panel */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-2 flex flex-col gap-3">
            {/* Label input */}
            <div>
              <label htmlFor={`label-${item.id}`} className="block text-[11px] font-medium text-gray-500 mb-1">
                ชื่อแท็บ
              </label>
              <input
                id={`label-${item.id}`}
                type="text"
                value={localLabel}
                onChange={(e) => handleLabelChange(e.target.value)}
                className="w-full h-9 rounded-sm border border-gray-200 px-3 text-[13px] text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Icon picker */}
            <div>
              <span className="block text-[11px] font-medium text-gray-500 mb-1">ไอคอน</span>
              <IconPickerPopover
                currentIcon={item.icon}
                onSelect={(iconName) => onUpdate({ icon: iconName })}
              />
            </div>

            {/* Route dropdown */}
            <div>
              <label htmlFor={`route-${item.id}`} className="block text-[11px] font-medium text-gray-500 mb-1">
                เส้นทาง
              </label>
              <div className="relative">
                <select
                  id={`route-${item.id}`}
                  value={item.route}
                  onChange={(e) => onUpdate({ route: e.target.value })}
                  disabled={isCartRoute}
                  className={`w-full h-9 rounded-sm border border-gray-200 px-3 text-[13px] text-gray-900 outline-none appearance-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                    isCartRoute ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''
                  }`}
                >
                  {ROUTE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {isCartRoute && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <Lock className="size-3.5 text-gray-400" />
                    <span className="text-[10px] text-gray-400">ล็อก</span>
                  </div>
                )}
              </div>
              {isCartRoute && (
                <p className="mt-1 text-[10px] text-gray-400">
                  ตะกร้าใช้เส้นทางนี้เสมอ
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
